import type { TieredDiversificationTier } from '@/components/molecules/diversification-constraint/types'

interface ValidationResult {
  isValid: boolean
  errors: { tierIndex: number; message: string }[]
}

export const validateTierMarketValues = (
  tiers: TieredDiversificationTier[],
): ValidationResult => {
  const errors: { tierIndex: number; message: string }[] = []

  for (let i = 0; i < tiers.length - 1; i++) {
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    const currentValue = tiers[i]!.maxMarketValue

    // Check for null/undefined values (except last tier)
    if (currentValue === null || currentValue === undefined) {
      errors.push({
        tierIndex: i,
        message: 'Market value is required',
      })
      continue
    }

    // Check minimum value
    if (currentValue <= 0) {
      errors.push({
        tierIndex: i,
        message: 'Must be greater than 0',
      })
      continue
    }

    // Check ascending order
    if (i > 0) {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      const previousValue = tiers[i - 1]!.maxMarketValue
      if (previousValue !== null && currentValue <= previousValue) {
        errors.push({
          tierIndex: i,
          message: 'Must be greater than previous tier',
        })
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

export const addTier = (
  tiers: TieredDiversificationTier[],
): TieredDiversificationTier[] => {
  // Insert new tier before the last (catch-all) tier
  const newTier: TieredDiversificationTier = {
    id: crypto.randomUUID(),
    maxMarketValue: null, // User must fill in
    weightRange: [0, 100] as [number, number],
  }
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  return [...tiers.slice(0, -1), newTier, tiers[tiers.length - 1]!]
}

export const removeTier = (
  tiers: TieredDiversificationTier[],
  index: number,
): TieredDiversificationTier[] => {
  // Cannot remove last tier (catch-all)
  if (index === tiers.length - 1) return tiers
  return tiers.filter((_, i) => i !== index)
}

export const getDefaultTiers = (
  defaultWeightRange: [number, number],
): TieredDiversificationTier[] => [
  {
    id: crypto.randomUUID(),
    maxMarketValue: 1000000,
    weightRange: [defaultWeightRange[0], defaultWeightRange[1]],
  },
  {
    id: crypto.randomUUID(),
    maxMarketValue: null,
    weightRange: [defaultWeightRange[0], defaultWeightRange[1]],
  },
]

export const getErrorForTier = (
  errors: ValidationResult['errors'],
  tierIndex: number,
): string | undefined => {
  const error = errors.find((e) => e.tierIndex === tierIndex)
  return error?.message
}
